package br.com.vvrefeicoes.portal.feature.notifications

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.decodeToImageBitmap
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.ui.components.AppModalCard
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.X
import com.mohamedrejeb.richeditor.model.rememberRichTextState
import com.mohamedrejeb.richeditor.ui.material3.RichText
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.allDrawableResources
import portalvv.shared.generated.resources.dismiss

private const val RESOURCE_PREFIX = "resource:"

/**
 * Modal reutilizável de comunicado: imagem centralizada, texto e um slot de ações. Usado tanto na
 * prévia da publicação quanto na notificação de prioridade alta.
 */
@Composable
fun AnnouncementModal(
    title: String,
    imageUrl: String?,
    bodyHtml: String,
    onDismiss: () -> Unit,
    actions: @Composable ColumnScope.() -> Unit,
) {
    AppModalCard(title = title, onDismiss = onDismiss) {
        AnnouncementImage(source = imageUrl, modifier = Modifier.fillMaxWidth().heightIn(max = 360.dp))
        RichBody(html = bodyHtml, modifier = Modifier.fillMaxWidth())
        actions()
    }
}

/** Renderiza o conteúdo do comunicado (HTML) de forma formatada. */
@Composable
fun RichBody(
    html: String,
    modifier: Modifier = Modifier,
    maxLines: Int = Int.MAX_VALUE,
    overflow: TextOverflow = TextOverflow.Clip,
) {
    val state = rememberRichTextState()
    LaunchedEffect(html) { runCatching { state.setHtml(html) } }
    val hasLink = html.contains("<a ", ignoreCase = true) || html.contains("<a>", ignoreCase = true)
    val linkTag = remember(state) { "rich-link-body-${state.hashCode()}" }
    val taggedModifier = if (hasLink) modifier.testTag(linkTag) else modifier
    RichText(state = state, modifier = taggedModifier, maxLines = maxLines, overflow = overflow)
}

/** Lightbox reutilizável: abre a imagem do comunicado em tela cheia; clique fora (ou no X) fecha. */
@Composable
fun AnnouncementImageLightbox(
    imageUrl: String,
    onDismiss: () -> Unit,
) {
    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(usePlatformDefaultWidth = false),
    ) {
        Box(
            modifier =
                Modifier
                    .fillMaxSize()
                    .background(Color.Black.copy(alpha = 0.85f))
                    .clickable { onDismiss() },
            contentAlignment = Alignment.Center,
        ) {
            AnnouncementImage(source = imageUrl, modifier = Modifier.fillMaxSize(0.9f))
            IconButton(
                onClick = onDismiss,
                modifier = Modifier.align(Alignment.TopEnd).padding(AppSpacing.space16),
            ) {
                Icon(
                    imageVector = Lucide.X,
                    contentDescription = stringResource(Res.string.dismiss),
                    tint = Color.White,
                )
            }
        }
    }
}

/** Renderiza a imagem do comunicado: `resource:<key>` vem dos resources; o resto é baixado por URL. */
@Composable
fun AnnouncementImage(
    source: String?,
    modifier: Modifier = Modifier,
) {
    if (source.isNullOrBlank()) return
    if (source.startsWith(RESOURCE_PREFIX)) {
        val drawable = Res.allDrawableResources[source.removePrefix(RESOURCE_PREFIX)]
        if (drawable != null) {
            Image(
                painter = painterResource(drawable),
                contentDescription = null,
                modifier = modifier,
                contentScale = ContentScale.Fit,
            )
        }
        return
    }
    val url = LocalAppGraph.current.absoluteUrl(source) ?: return
    RemoteImage(url = url, modifier = modifier)
}

@Composable
internal fun RemoteImage(
    url: String,
    modifier: Modifier = Modifier,
) {
    val appGraph = LocalAppGraph.current
    var bitmap by remember(url) { mutableStateOf<ImageBitmap?>(null) }
    LaunchedEffect(url) {
        bitmap = runCatching { appGraph.loadImageBytes(url)?.decodeToImageBitmap() }.getOrNull()
    }
    val image = bitmap ?: return
    Image(
        bitmap = image,
        contentDescription = null,
        modifier = modifier,
        contentScale = ContentScale.Fit,
    )
}

/** Prévia em texto simples (banner/sininho), sem tags HTML. */
internal fun htmlToPlain(value: String): String =
    value
        .replace(Regex("<[^>]*>"), " ")
        .replace("&nbsp;", " ")
        .replace("&amp;", "&")
        .replace("&lt;", "<")
        .replace("&gt;", ">")
        .replace(Regex("\\s+"), " ")
        .trim()

/** Lista todas as imagens empacotadas em resources, para o seletor do comunicado. */
internal fun bundledImageKeys(): List<String> = Res.allDrawableResources.keys.sorted()

@Composable
internal fun BundledImagePreview(
    key: String,
    modifier: Modifier = Modifier,
) {
    val drawable = Res.allDrawableResources[key] ?: return
    Image(
        painter = painterResource(drawable),
        contentDescription = null,
        modifier = modifier,
        contentScale = ContentScale.Fit,
    )
}
